import { useEffect, useState, type ReactNode } from 'react';
import { Download, Film, Image as ImageIcon, ListOrdered, Paperclip, Play, Trash2 } from '@/components/icons';
import { fetchArtifactBlob } from '@/lib/api';
import { artifactLabel, formatBytes, groupArtifacts } from '@/lib/artifacts';
import type { Artifact } from '@/lib/types';
import { Button, Dialog, useConfirm } from '@/components/ui';
import { ArtifactThumb, useArtifactUrl } from './ArtifactThumb';

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-slate-500 uppercase">
        {icon} {title}
      </h4>
      {children}
    </section>
  );
}

function VideoArtifact({
  resultId,
  artifact,
  publicToken,
  onDelete,
}: {
  resultId: string;
  artifact: Artifact;
  publicToken?: string;
  onDelete?: () => void;
}) {
  const [load, setLoad] = useState(false);
  const { url, failed, loading } = useArtifactUrl(resultId, artifact.name, publicToken, load);
  const label = artifactLabel(artifact.name);
  const confirm = useConfirm();
  const deleteButton = onDelete && (
    <Button
      size="sm"
      variant="ghost"
      className="no-print text-rose-600 hover:text-rose-700 dark:text-rose-400"
      onClick={async () => {
        const ok = await confirm({
          title: 'Delete this video?',
          message: `The video ${label} will be deleted from this report. The rest of the report stays. This cannot be undone.`,
          confirmLabel: 'Delete video',
        });
        if (ok) onDelete();
      }}
      icon={<Trash2 className="size-4" />}
    >
      Delete video
    </Button>
  );

  if (url) {
    return (
      <figure className="space-y-1">
        <video controls src={url} className="max-h-80 w-full max-w-xl rounded-xl bg-black ring-1 ring-slate-200 dark:ring-slate-700" aria-label={`Video ${label}`} />
        <figcaption className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {label}
          {deleteButton}
        </figcaption>
      </figure>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button size="sm" variant="secondary" loading={loading} onClick={() => setLoad(true)} icon={<Play className="size-4" />}>
        Play {label}
        {artifact.size ? ` (${formatBytes(artifact.size)})` : ''}
      </Button>
      {deleteButton}
      {failed && (
        <span className="text-xs text-rose-600" role="alert">
          The video could not be loaded.
        </span>
      )}
    </div>
  );
}

function TraceArtifact({ resultId, artifact, publicToken }: { resultId: string; artifact: Artifact; publicToken?: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || text !== null) return;
    let cancelled = false;
    fetchArtifactBlob(resultId, artifact.name, publicToken)
      .then((b) => b.text())
      .then((t) => !cancelled && setText(t))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [open, text, resultId, artifact.name, publicToken]);

  return (
    <details
      className="group rounded-xl bg-slate-50 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:ring-slate-700"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-slate-700 select-none hover:text-brand-700 dark:text-slate-200">
        Step-by-step trace <span className="font-normal text-slate-400">· {artifactLabel(artifact.name)}</span>
      </summary>
      <div className="px-4 pb-4" aria-live="polite">
        {failed ? (
          <p className="text-sm text-rose-600">The trace could not be loaded.</p>
        ) : text === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <pre className="max-h-[28rem] overflow-auto rounded-lg bg-white p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800">
            {text}
          </pre>
        )}
      </div>
    </details>
  );
}

function DownloadArtifact({ resultId, artifact, publicToken }: { resultId: string; artifact: Artifact; publicToken?: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const download = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const blob = await fetchArtifactBlob(resultId, artifact.name, publicToken);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = artifact.name.split('/').pop() ?? artifact.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex items-center gap-3 text-sm">
      <button
        type="button"
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-link underline-offset-2 hover:underline disabled:opacity-40"
      >
        <Download className="size-3.5" /> {artifactLabel(artifact.name)}
      </button>
      {artifact.size ? <span className="text-xs text-slate-400">{formatBytes(artifact.size)}</span> : null}
      {failed && <span className="text-xs text-rose-600">Download failed</span>}
    </li>
  );
}

/** Test evidence for one result: screenshots, videos, the step trace and other files. */
export function ArtifactGallery({
  resultId,
  artifacts,
  publicToken,
  onDelete,
}: {
  resultId: string;
  artifacts: Artifact[];
  publicToken?: string;
  /** Set when the viewer may delete videos. */
  onDelete?: (name: string) => void;
}) {
  const g = groupArtifacts(artifacts);
  const [lightbox, setLightbox] = useState<{ url: string; label: string } | null>(null);

  return (
    <div className="space-y-4">
      {g.screenshots.length > 0 && (
        <Section icon={<ImageIcon className="size-3.5" />} title={`Screenshots (${g.screenshots.length})`}>
          <div className="flex flex-wrap gap-3">
            {g.screenshots.map((a) => (
              <ArtifactThumb
                key={a.name}
                resultId={resultId}
                artifact={a}
                publicToken={publicToken}
                onOpen={(url) => setLightbox({ url, label: artifactLabel(a.name) })}
              />
            ))}
          </div>
        </Section>
      )}
      {g.videos.length > 0 && (
        <Section icon={<Film className="size-3.5" />} title="Video">
          <div className="space-y-3">
            {g.videos.map((a) => (
              <VideoArtifact
                key={a.name}
                resultId={resultId}
                artifact={a}
                publicToken={publicToken}
                onDelete={onDelete && (() => onDelete(a.name))}
              />
            ))}
          </div>
        </Section>
      )}
      {g.traces.length > 0 && (
        <Section icon={<ListOrdered className="size-3.5" />} title="Step trace">
          <div className="space-y-2">
            {g.traces.map((a) => (
              <TraceArtifact key={a.name} resultId={resultId} artifact={a} publicToken={publicToken} />
            ))}
          </div>
        </Section>
      )}
      {g.other.length > 0 && (
        <Section icon={<Paperclip className="size-3.5" />} title="Other files">
          <ul className="space-y-1.5">
            {g.other.map((a) => (
              <DownloadArtifact key={a.name} resultId={resultId} artifact={a} publicToken={publicToken} />
            ))}
          </ul>
        </Section>
      )}

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} size="lg" title={lightbox?.label ?? 'Screenshot'}>
        {lightbox && <img src={lightbox.url} alt={lightbox.label} className="w-full rounded-xl ring-1 ring-slate-200 dark:ring-slate-700" />}
      </Dialog>
    </div>
  );
}
